import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { useToast } from "@/hooks/use-toast";
import { 
  Phone, Mail, Building, MessageSquare, CheckCircle, XCircle, Clock, 
  Globe, Users, Calendar, FileText, User, Send, ArrowRight, BookOpen, 
  Shield, CreditCard, Target, Package, FileCheck, Briefcase, MapPin, 
  Music, Utensils, Palette, Ticket, Sparkles, Store, Building2
} from "lucide-react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";

interface Consultation {
  id: string;
  name: string;
  email: string;
  phone?: string | null;
  company?: string | null;
  service_type: string;
  message?: string | null;
  status: string;
  created_at: string;
  notes?: string | null;
  business_registration_number?: string | null;
  tax_id?: string | null;
  business_address?: string | null;
  website?: string | null;
  industry?: string | null;
  number_of_employees?: string | null;
  years_in_business?: number | null;
  business_description?: string | null;
  primary_contact_position?: string | null;
  venue_type?: string | null;
  venue_capacity?: number | null;
  alcohol_license?: boolean | null;
  music_license?: boolean | null;
  operating_hours?: string | null;
  event_frequency?: string | null;
  company_id?: string | null;
  approved_at?: string | null;
  approved_by?: string | null;
}

const onboardingSteps = {
  night_club: [
    { step: 1, title: "License Verification", description: "Verify music & alcohol licenses", icon: FileCheck },
    { step: 2, title: "Capacity Assessment", description: "Review venue capacity and safety", icon: Users },
    { step: 3, title: "Sound System Setup", description: "Configure audio equipment", icon: Music },
    { step: 4, title: "DJ/Artist Network", description: "Connect with performers", icon: Sparkles },
    { step: 5, title: "Security Protocol", description: "Establish safety measures", icon: Shield },
    { step: 6, title: "Promotion Setup", description: "Launch marketing campaigns", icon: Target },
  ],
  restaurant: [
    { step: 1, title: "Menu Integration", description: "Upload menu and pricing", icon: Utensils },
    { step: 2, title: "Reservation System", description: "Set up booking platform", icon: Calendar },
    { step: 3, title: "Staff Training", description: "Train team on MCG platform", icon: Users },
    { step: 4, title: "Event Planning", description: "Create special event calendar", icon: Sparkles },
    { step: 5, title: "Compliance Check", description: "Verify food safety licenses", icon: Shield },
    { step: 6, title: "Marketing Launch", description: "Promote dining experiences", icon: Target },
  ],
  gallery: [
    { step: 1, title: "Artwork Catalog", description: "Digitize art collection", icon: Palette },
    { step: 2, title: "Virtual Tour Setup", description: "Create 360° gallery view", icon: Globe },
    { step: 3, title: "Artist Profiles", description: "Create artist portfolios", icon: User },
    { step: 4, title: "Exhibition Calendar", description: "Schedule shows and events", icon: Calendar },
    { step: 5, title: "Sales Integration", description: "Set up art sales platform", icon: CreditCard },
    { step: 6, title: "Marketing Launch", description: "Promote exhibitions", icon: Target },
  ],
  theater: [
    { step: 1, title: "Seating Plan", description: "Configure seating layout", icon: Users },
    { step: 2, title: "Performance Calendar", description: "Schedule shows and rehearsals", icon: Calendar },
    { step: 3, title: "Ticket System", description: "Set up ticketing platform", icon: Ticket },
    { step: 4, title: "Cast Management", description: "Register performers", icon: User },
    { step: 5, title: "Production Setup", description: "Configure backstage tools", icon: Package },
    { step: 6, title: "Marketing Launch", description: "Promote performances", icon: Target },
  ],
  festival: [
    { step: 1, title: "Venue Layout", description: "Plan festival grounds", icon: MapPin },
    { step: 2, title: "Artist Lineup", description: "Book and schedule performers", icon: Music },
    { step: 3, title: "Vendor Setup", description: "Register food and merch vendors", icon: Store },
    { step: 4, title: "Ticket Tiers", description: "Create pricing and packages", icon: Ticket },
    { step: 5, title: "Safety Planning", description: "Emergency and security plans", icon: Shield },
    { step: 6, title: "Marketing Campaign", description: "Launch promotion", icon: Target },
  ],
  pop_up: [
    { step: 1, title: "Location Setup", description: "Confirm venue and dates", icon: MapPin },
    { step: 2, title: "Theme Design", description: "Create event concept", icon: Palette },
    { step: 3, title: "Vendor Coordination", description: "Organize suppliers", icon: Store },
    { step: 4, title: "Ticketing Setup", description: "Create limited tickets", icon: Ticket },
    { step: 5, title: "Marketing Blitz", description: "Short-term promotion", icon: Target },
    { step: 6, title: "Operations Plan", description: "Day-of coordination", icon: Briefcase },
  ],
};

const complianceRequirements = [
  { requirement: "Business Registration Certificate (CIPC)", description: "Official company registration document from CIPC", category: "Legal" },
  { requirement: "Tax Clearance Certificate", description: "SARS tax compliance certificate", category: "Legal" },
  { requirement: "B-BBEE Certificate", description: "Broad-Based Black Economic Empowerment certificate", category: "Legal" },
  { requirement: "COIDA Registration", description: "Compensation for Occupational Injuries and Diseases Act", category: "Legal" },
  { requirement: "Director ID Documents", description: "Certified copies of director/owner ID documents", category: "Identity" },
  { requirement: "Proof of Address", description: "Business address verification (utility bill or lease agreement)", category: "Identity" },
  { requirement: "Bank Confirmation Letter", description: "Letter confirming business banking details", category: "Financial" },
  { requirement: "Financial Statements", description: "Last 12 months financial statements or management accounts", category: "Financial" },
  { requirement: "Professional Indemnity Insurance", description: "Coverage for professional liability", category: "Insurance" },
  { requirement: "Public Liability Insurance", description: "Coverage for third-party claims", category: "Insurance" },
  { requirement: "POPI Act Compliance Declaration", description: "Protection of Personal Information Act compliance", category: "Compliance" },
  { requirement: "PAIA Manual", description: "Promotion of Access to Information Act manual", category: "Compliance" },
  { requirement: "Service Level Agreement Acceptance", description: "Signed MCG Agency Partner SLA", category: "Contract" },
  { requirement: "Code of Conduct Acknowledgment", description: "Signed MCG Agency Code of Conduct", category: "Contract" },
  { requirement: "Creator/Venue Management Experience", description: "Portfolio or references of previous management work", category: "Experience" },
];

const complianceCategories = ["Legal", "Identity", "Financial", "Insurance", "Compliance", "Contract", "Experience"];

const ConsultationManagement = () => {
  const [consultations, setConsultations] = useState<Consultation[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedConsultation, setSelectedConsultation] = useState<Consultation | null>(null);
  const [notes, setNotes] = useState("");
  const [showOnboarding, setShowOnboarding] = useState(false);
  const [showCompliance, setShowCompliance] = useState(false);
  const [currentStep, setCurrentStep] = useState(1);
  const { toast } = useToast();

  useEffect(() => {
    fetchConsultations();
  }, []);

  const fetchConsultations = async () => {
    try {
      const { data, error } = await supabase
        .from("consultations")
        .select("*")
        .order("created_at", { ascending: false });

      if (error) throw error;
      setConsultations((data || []) as Consultation[]);
    } catch (error: any) {
      toast({
        variant: "destructive",
        title: "Error loading consultations",
        description: error.message,
      });
    } finally {
      setLoading(false);
    }
  };

  const updateStatus = async (id: string, status: string) => {
    try {
      const consultation = consultations.find(c => c.id === id);
      
      // Generate company ID on approval
      let companyId = null;
      if (status === "approved" && consultation && !consultation.company_id) {
        // Generate unique company ID
        const { data: generatedId, error: genError } = await supabase.rpc('generate_company_id');
        if (genError) throw genError;
        companyId = generatedId;
      }

      const { data: { user } } = await supabase.auth.getUser();

      const updateData: any = { 
        status, 
        notes: notes || undefined,
      };
      
      if (companyId) {
        updateData.company_id = companyId;
        updateData.approved_at = new Date().toISOString();
        updateData.approved_by = user?.id;
      }

      const { error } = await supabase
        .from("consultations")
        .update(updateData)
        .eq("id", id);

      if (error) throw error;

      if (status === "approved" && consultation) {
        await createAgencyAccount(consultation, companyId);
      }

      toast({
        title: "Status updated",
        description: status === "approved" 
          ? `Consultation approved. Company ID: ${companyId}` 
          : `Consultation marked as ${status}`,
      });

      fetchConsultations();
      setSelectedConsultation(null);
      setNotes("");
    } catch (error: any) {
      toast({
        variant: "destructive",
        title: "Error updating status",
        description: error.message,
      });
    }
  };

  const createAgencyAccount = async (consultation: Consultation, companyId: string | null) => {
    try {
      // Use edge function for secure account creation with crypto-secure password
      const { data, error } = await supabase.functions.invoke('create-agency-account', {
        body: {
          email: consultation.email,
          name: consultation.name,
          company: consultation.company,
          serviceType: consultation.service_type,
          companyId: companyId,
        },
      });

      if (error) throw error;

      if (!data?.success) {
        throw new Error(data?.error || 'Failed to create account');
      }

      // TODO: In production, the edge function should send the temp password via secure email
      // For now, the admin can manually communicate credentials securely
      
      toast({
        title: "Agency account created",
        description: `Account created for ${consultation.email}. Credentials will be sent via secure email.`,
      });

    } catch (error: any) {
      console.error("Error creating agency account:", error);
      toast({
        variant: "destructive",
        title: "Error creating agency account",
        description: error.message,
      });
    }
  };

  const getVenueTypeIcon = (type: string | null) => {
    switch (type) {
      case 'night_club': return <Music className="w-4 h-4" />;
      case 'restaurant': return <Utensils className="w-4 h-4" />;
      case 'gallery': return <Palette className="w-4 h-4" />;
      case 'theater': return <Ticket className="w-4 h-4" />;
      case 'festival': return <Sparkles className="w-4 h-4" />;
      case 'pop_up': return <Store className="w-4 h-4" />;
      default: return <Building2 className="w-4 h-4" />;
    }
  };

  const getVenueTypeLabel = (type: string | null) => {
    switch (type) {
      case 'night_club': return 'Night Club';
      case 'restaurant': return 'Restaurant';
      case 'gallery': return 'Art Gallery';
      case 'theater': return 'Theater';
      case 'festival': return 'Festival';
      case 'pop_up': return 'Pop-Up Event';
      default: return 'Venue';
    }
  };

  const getStatusBadge = (status: string) => {
    const variants: Record<string, { color: string; icon: any }> = {
      pending: { color: "bg-gradient-to-r from-yellow-500/20 to-yellow-600/10 text-yellow-300 border-yellow-500/30", icon: Clock },
      contacted: { color: "bg-gradient-to-r from-blue-500/20 to-blue-600/10 text-blue-300 border-blue-500/30", icon: MessageSquare },
      approved: { color: "bg-gradient-to-r from-green-500/20 to-green-600/10 text-green-300 border-green-500/30", icon: CheckCircle },
      rejected: { color: "bg-gradient-to-r from-red-500/20 to-red-600/10 text-red-300 border-red-500/30", icon: XCircle },
      onboarding: { color: "bg-gradient-to-r from-purple-500/20 to-purple-600/10 text-purple-300 border-purple-500/30", icon: ArrowRight },
    };

    const StatusIcon = variants[status]?.icon || Clock;
    return (
      <Badge className={`${variants[status]?.color || ""} flex items-center gap-1 border`}>
        <StatusIcon className="w-3 h-3" />
        {status.charAt(0).toUpperCase() + status.slice(1)}
      </Badge>
    );
  };

  const getServiceLabel = (type: string) => {
    const labels: Record<string, string> = {
      creator_management: "Creator Management",
      venue_oversight: "Venue Oversight",
      both: "Both Services",
    };
    return labels[type] || type;
  };

  const getOnboardingSteps = (venueType: string | null) => {
    if (!venueType || !onboardingSteps[venueType as keyof typeof onboardingSteps]) {
      return onboardingSteps.night_club; // Default to night club
    }
    return onboardingSteps[venueType as keyof typeof onboardingSteps];
  };

  const startOnboarding = async (consultationId: string) => {
    try {
      await supabase
        .from("consultations")
        .update({ status: "onboarding" })
        .eq("id", consultationId);

      toast({
        title: "Onboarding started",
        description: "Venue onboarding process initiated",
      });

      fetchConsultations();
      setShowOnboarding(true);
    } catch (error: any) {
      toast({
        variant: "destructive",
        title: "Error starting onboarding",
        description: error.message,
      });
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center space-y-4">
          <div className="w-12 h-12 border-4 border-gray-800 border-t-white rounded-full animate-spin mx-auto"></div>
          <p className="text-gray-400">Loading consultations...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold bg-gradient-to-r from-white to-gray-400 bg-clip-text text-transparent">
            Consultation & Onboarding Management
          </h2>
          <p className="text-gray-400">Manage enterprise consultation inquiries & onboard new venues</p>
        </div>
        <div className="flex items-center gap-4">
          <Badge className="bg-gradient-to-r from-gray-800 to-black border border-gray-700 shadow-lg">
            {consultations.length} Total Requests
          </Badge>
          <Dialog open={showCompliance} onOpenChange={setShowCompliance}>
            <DialogTrigger asChild>
              <Button variant="outline" className="border-gray-700 text-gray-300 hover:text-white hover:border-gray-500">
                <Shield className="w-4 h-4 mr-2" />
                Compliance Checklist
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-gradient-to-br from-gray-900 to-black border border-gray-800 max-w-5xl max-h-[85vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle className="text-white flex items-center gap-2">
                  <Shield className="w-5 h-5" />
                  Agency Partner Compliance Requirements
                </DialogTitle>
                <DialogDescription className="text-gray-400">
                  Required documents and verification for agency partner registration. Use this checklist when reviewing applications.
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-6">
                {complianceCategories.map((category) => (
                  <div key={category} className="space-y-3">
                    <h3 className="text-sm font-semibold text-white uppercase tracking-wider border-b border-gray-800 pb-2">
                      {category} Requirements
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      {complianceRequirements
                        .filter((req) => req.category === category)
                        .map((req, index) => (
                          <div key={index} className="flex items-start gap-3 p-3 rounded-lg bg-gradient-to-r from-gray-800/50 to-gray-900/50 border border-gray-800">
                            <div className="flex-shrink-0 w-6 h-6 rounded-full bg-gradient-to-r from-gray-700 to-gray-800 flex items-center justify-center mt-0.5">
                              <FileCheck className="w-3 h-3 text-gray-400" />
                            </div>
                            <div>
                              <h4 className="font-medium text-white text-sm">{req.requirement}</h4>
                              <p className="text-xs text-gray-400 mt-0.5">{req.description}</p>
                            </div>
                          </div>
                        ))}
                    </div>
                  </div>
                ))}
                
                <div className="pt-4 border-t border-gray-800">
                  <p className="text-sm text-gray-400">
                    <strong className="text-white">Note:</strong> Not all requirements may apply to every agency. 
                    Review the applicant's service type (Creator Management / Venue Oversight) to determine applicable requirements.
                  </p>
                </div>
              </div>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Onboarding Flow Modal */}
      <Dialog open={showOnboarding} onOpenChange={setShowOnboarding}>
        <DialogContent className="bg-gradient-to-br from-gray-900 to-black border border-gray-800 max-w-4xl">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <BookOpen className="w-5 h-5" />
              Venue Onboarding Process
            </DialogTitle>
            <DialogDescription className="text-gray-400">
              Step-by-step guide to onboard {selectedConsultation?.company} to the MCG platform
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-6">
            {/* Progress Bar */}
            <div className="space-y-2">
              <div className="flex justify-between text-sm">
                <span className="text-gray-300">Progress</span>
                <span className="text-white font-medium">Step {currentStep} of 6</span>
              </div>
              <div className="h-2 bg-gray-800 rounded-full overflow-hidden">
                <div 
                  className="h-full bg-gradient-to-r from-white to-gray-300 transition-all duration-300"
                  style={{ width: `${(currentStep / 6) * 100}%` }}
                />
              </div>
            </div>

            {/* Steps Grid */}
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              {getOnboardingSteps(selectedConsultation?.venue_type).map((step) => {
                const StepIcon = step.icon;
                const isCompleted = step.step < currentStep;
                const isCurrent = step.step === currentStep;
                
                return (
                  <div 
                    key={step.step}
                    className={`p-4 rounded-xl border transition-all duration-200 ${
                      isCurrent 
                        ? 'bg-gradient-to-r from-gray-800 to-gray-900 border-white scale-105 shadow-lg'
                        : isCompleted
                        ? 'bg-gradient-to-r from-gray-900 to-black border-gray-700 opacity-80'
                        : 'bg-gradient-to-r from-gray-900/50 to-black/50 border-gray-800 opacity-60'
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                        isCurrent ? 'bg-gradient-to-r from-white to-gray-300' : 'bg-gray-800'
                      }`}>
                        <StepIcon className={`w-5 h-5 ${isCurrent ? 'text-black' : 'text-gray-400'}`} />
                      </div>
                      <div>
                        <div className="flex items-center gap-2">
                          <span className={`text-sm font-medium ${
                            isCurrent ? 'text-white' : 'text-gray-400'
                          }`}>
                            Step {step.step}
                          </span>
                          {isCompleted && (
                            <CheckCircle className="w-4 h-4 text-green-400" />
                          )}
                        </div>
                        <h4 className={`font-semibold ${
                          isCurrent ? 'text-white' : 'text-gray-300'
                        }`}>
                          {step.title}
                        </h4>
                        <p className="text-sm text-gray-400">{step.description}</p>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Action Buttons */}
            <div className="flex justify-between pt-4 border-t border-gray-800">
              <Button
                variant="outline"
                onClick={() => setCurrentStep(prev => Math.max(1, prev - 1))}
                disabled={currentStep === 1}
                className="border-gray-700 text-gray-300 hover:text-white hover:border-gray-500"
              >
                Previous Step
              </Button>
              
              <div className="flex gap-3">
                <Button
                  variant="outline"
                  onClick={() => setShowOnboarding(false)}
                  className="border-gray-700 text-gray-300 hover:text-white hover:border-gray-500"
                >
                  Save & Exit
                </Button>
                
                {currentStep < 6 ? (
                  <Button
                    onClick={() => setCurrentStep(prev => prev + 1)}
                    className="bg-gradient-to-r from-white to-gray-300 hover:from-gray-200 hover:to-gray-400 text-black font-semibold"
                  >
                    Next Step
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                ) : (
                  <Button
                    onClick={() => {
                      toast({
                        title: "Onboarding Complete!",
                        description: `${selectedConsultation?.company} has been successfully onboarded`,
                      });
                      setShowOnboarding(false);
                    }}
                    className="bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 text-white font-semibold"
                  >
                    Complete Onboarding
                    <CheckCircle className="w-4 h-4 ml-2" />
                  </Button>
                )}
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Consultations List */}
      <div className="grid gap-6">
        {consultations.map((consultation) => (
          <Card key={consultation.id} className="bg-gradient-to-br from-gray-900 to-black border border-gray-800 shadow-2xl overflow-hidden">
            <CardHeader className="border-b border-gray-800">
              <div className="flex items-start justify-between">
                <div className="space-y-2">
                  <div className="flex items-center gap-3">
                    <CardTitle className="text-white">{consultation.company || consultation.name}</CardTitle>
                    {consultation.venue_type && (
                      <Badge className="bg-gradient-to-r from-gray-800 to-black border border-gray-700 flex items-center gap-1">
                        {getVenueTypeIcon(consultation.venue_type)}
                        {getVenueTypeLabel(consultation.venue_type)}
                      </Badge>
                    )}
                  </div>
                  
                  <div className="flex items-center gap-4 text-sm">
                    <div className="flex items-center gap-1 text-gray-400">
                      <User className="w-3 h-3" />
                      {consultation.name}
                      {consultation.primary_contact_position && ` • ${consultation.primary_contact_position}`}
                    </div>
                    <div className="flex items-center gap-1 text-gray-400">
                      <Calendar className="w-3 h-3" />
                      {new Date(consultation.created_at).toLocaleDateString("en-US", {
                        year: "numeric",
                        month: "short",
                        day: "numeric",
                        hour: "2-digit",
                        minute: "2-digit",
                      })}
                    </div>
                  </div>
                </div>
                
                <div className="flex flex-col items-end gap-2">
                  {getStatusBadge(consultation.status)}
                  <Badge variant="secondary" className="bg-gradient-to-r from-gray-800 to-black border border-gray-700">
                    {getServiceLabel(consultation.service_type)}
                  </Badge>
                </div>
              </div>
            </CardHeader>
            
            <CardContent className="space-y-6 pt-6">
              {/* Contact & Business Info */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <div className="space-y-4">
                  <div className="bg-gradient-to-r from-gray-800/50 to-gray-900/50 p-4 rounded-xl border border-gray-800">
                    <h3 className="font-semibold text-white flex items-center gap-2 mb-3">
                      <Mail className="w-4 h-4" />
                      Contact Information
                    </h3>
                    <div className="space-y-3">
                      <div className="flex items-center gap-2 text-sm">
                        <Mail className="w-4 h-4 text-gray-400" />
                        <a href={`mailto:${consultation.email}`} className="text-gray-300 hover:text-white hover:underline">
                          {consultation.email}
                        </a>
                      </div>
                      {consultation.phone && (
                        <div className="flex items-center gap-2 text-sm">
                          <Phone className="w-4 h-4 text-gray-400" />
                          <a href={`tel:${consultation.phone}`} className="text-gray-300 hover:text-white hover:underline">
                            {consultation.phone}
                          </a>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Venue Specific Info */}
                  {(consultation.venue_capacity || consultation.operating_hours || consultation.event_frequency) && (
                    <div className="bg-gradient-to-r from-gray-800/50 to-gray-900/50 p-4 rounded-xl border border-gray-800">
                      <h3 className="font-semibold text-white flex items-center gap-2 mb-3">
                        <Building2 className="w-4 h-4" />
                        Venue Details
                      </h3>
                      <div className="grid grid-cols-2 gap-3 text-sm">
                        {consultation.venue_capacity && (
                          <div>
                            <p className="text-gray-400">Capacity</p>
                            <p className="font-medium text-white">{consultation.venue_capacity} people</p>
                          </div>
                        )}
                        {consultation.operating_hours && (
                          <div>
                            <p className="text-gray-400">Operating Hours</p>
                            <p className="font-medium text-white">{consultation.operating_hours}</p>
                          </div>
                        )}
                        {consultation.event_frequency && (
                          <div className="col-span-2">
                            <p className="text-gray-400">Event Frequency</p>
                            <p className="font-medium text-white">{consultation.event_frequency}</p>
                          </div>
                        )}
                        {(consultation.alcohol_license !== null || consultation.music_license !== null) && (
                          <div className="col-span-2 space-y-2">
                            <p className="text-gray-400">Licenses</p>
                            <div className="flex gap-3">
                              {consultation.alcohol_license && (
                                <Badge className="bg-gradient-to-r from-green-500/20 to-green-600/10 text-green-300 border-green-500/30">
                                  Alcohol License ✓
                                </Badge>
                              )}
                              {consultation.music_license && (
                                <Badge className="bg-gradient-to-r from-blue-500/20 to-blue-600/10 text-blue-300 border-blue-500/30">
                                  Music License ✓
                                </Badge>
                              )}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>

                <div className="bg-gradient-to-r from-gray-800/50 to-gray-900/50 p-4 rounded-xl border border-gray-800">
                  <h3 className="font-semibold text-white flex items-center gap-2 mb-3">
                    <Briefcase className="w-4 h-4" />
                    Business Details
                  </h3>
                  <div className="grid grid-cols-2 gap-3 text-sm">
                    {consultation.business_registration_number && (
                      <div>
                        <p className="text-gray-400">Registration #</p>
                        <p className="font-medium text-white">{consultation.business_registration_number}</p>
                      </div>
                    )}
                    {consultation.tax_id && (
                      <div>
                        <p className="text-gray-400">Tax ID</p>
                        <p className="font-medium text-white">{consultation.tax_id}</p>
                      </div>
                    )}
                    {consultation.years_in_business && (
                      <div>
                        <p className="text-gray-400">Years in Business</p>
                        <p className="font-medium text-white">{consultation.years_in_business} years</p>
                      </div>
                    )}
                    {consultation.number_of_employees && (
                      <div>
                        <p className="text-gray-400">Employees</p>
                        <p className="font-medium text-white">{consultation.number_of_employees}</p>
                      </div>
                    )}
                    {consultation.website && (
                      <div className="col-span-2">
                        <p className="text-gray-400 flex items-center gap-1">
                          <Globe className="w-3 h-3" />
                          Website
                        </p>
                        <a 
                          href={consultation.website.startsWith('http') ? consultation.website : `https://${consultation.website}`}
                          target="_blank"
                          rel="noopener noreferrer" 
                          className="font-medium text-gray-300 hover:text-white hover:underline break-all"
                        >
                          {consultation.website}
                        </a>
                      </div>
                    )}
                    {consultation.business_address && (
                      <div className="col-span-2">
                        <p className="text-gray-400">Business Address</p>
                        <p className="font-medium text-white">{consultation.business_address}</p>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Descriptions */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {consultation.business_description && (
                  <div className="bg-gradient-to-r from-gray-800/50 to-gray-900/50 p-4 rounded-xl border border-gray-800">
                    <h3 className="font-semibold text-white flex items-center gap-2 mb-3">
                      <FileText className="w-4 h-4" />
                      Business Description
                    </h3>
                    <p className="text-sm text-gray-300">{consultation.business_description}</p>
                  </div>
                )}
                
                {consultation.message && (
                  <div className="bg-gradient-to-r from-gray-800/50 to-gray-900/50 p-4 rounded-xl border border-gray-800">
                    <h3 className="font-semibold text-white flex items-center gap-2 mb-3">
                      <MessageSquare className="w-4 h-4" />
                      Additional Message
                    </h3>
                    <p className="text-sm text-gray-300">{consultation.message}</p>
                  </div>
                )}
              </div>

              {/* Admin Notes */}
              {consultation.notes && (
                <div className="bg-gradient-to-r from-gray-800 to-black p-4 rounded-xl border border-gray-700">
                  <p className="text-sm font-semibold text-white mb-2 flex items-center gap-2">
                    <FileText className="w-4 h-4" />
                    Admin Notes:
                  </p>
                  <p className="text-sm text-gray-300">{consultation.notes}</p>
                </div>
              )}

              {/* Action Buttons */}
              <div className="flex flex-wrap gap-3 pt-4 border-t border-gray-800">
                {selectedConsultation?.id === consultation.id ? (
                  <div className="space-y-3 w-full">
                    <Textarea
                      placeholder="Add notes about this consultation (e.g., verification details, compliance check, next steps)..."
                      value={notes}
                      onChange={(e) => setNotes(e.target.value)}
                      className="min-h-[100px] bg-gray-900 border-gray-700 text-white placeholder:text-gray-500"
                    />
                    <div className="flex flex-wrap gap-2">
                      <Button
                        onClick={() => updateStatus(consultation.id, "contacted")}
                        className="bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white shadow-lg"
                      >
                        <MessageSquare className="w-4 h-4 mr-2" />
                        Mark Contacted
                      </Button>
                      <Button
                        onClick={() => updateStatus(consultation.id, "approved")}
                        className="bg-gradient-to-r from-green-600 to-green-700 hover:from-green-700 hover:to-green-800 text-white shadow-lg"
                      >
                        <CheckCircle className="w-4 h-4 mr-2" />
                        Approve & Create Account
                      </Button>
                      <Button
                        onClick={() => {
                          setSelectedConsultation(consultation);
                          startOnboarding(consultation.id);
                        }}
                        className="bg-gradient-to-r from-purple-600 to-purple-700 hover:from-purple-700 hover:to-purple-800 text-white shadow-lg"
                      >
                        <BookOpen className="w-4 h-4 mr-2" />
                        Start Onboarding
                      </Button>
                      <Button
                        onClick={() => updateStatus(consultation.id, "rejected")}
                        variant="destructive"
                        className="shadow-lg"
                      >
                        <XCircle className="w-4 h-4 mr-2" />
                        Reject
                      </Button>
                      <Button
                        onClick={() => {
                          setSelectedConsultation(null);
                          setNotes("");
                        }}
                        variant="outline"
                        className="border-gray-700 text-gray-300 hover:text-white hover:border-gray-500"
                      >
                        Cancel
                      </Button>
                    </div>
                  </div>
                ) : (
                  <>
                    <Button
                      onClick={() => {
                        setSelectedConsultation(consultation);
                        setNotes(consultation.notes || "");
                      }}
                      className="bg-gradient-to-r from-white to-gray-300 hover:from-gray-200 hover:to-gray-400 text-black font-semibold shadow-lg"
                    >
                      <Send className="w-4 h-4 mr-2" />
                      Review & Update
                    </Button>
                    
                    {consultation.status === "approved" && (
                      <Button
                        onClick={() => {
                          setSelectedConsultation(consultation);
                          startOnboarding(consultation.id);
                        }}
                        className="bg-gradient-to-r from-purple-600 to-purple-700 hover:from-purple-700 hover:to-purple-800 text-white shadow-lg"
                      >
                        <BookOpen className="w-4 h-4 mr-2" />
                        Begin Onboarding
                      </Button>
                    )}
                    
                    <Button
                      variant="outline"
                      onClick={() => setShowCompliance(true)}
                      className="border-gray-700 text-gray-300 hover:text-white hover:border-gray-500"
                    >
                      <Shield className="w-4 h-4 mr-2" />
                      Check Compliance
                    </Button>
                  </>
                )}
              </div>
            </CardContent>
          </Card>
        ))}

        {consultations.length === 0 && (
          <Card className="bg-gradient-to-br from-gray-900 to-black border border-gray-800">
            <CardContent className="py-16 text-center">
              <MessageSquare className="w-16 h-16 text-gray-600 mx-auto mb-6" />
              <p className="text-gray-400 text-lg mb-2">No consultation requests yet</p>
              <p className="text-gray-500 text-sm">New consultation requests will appear here</p>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
};

export default ConsultationManagement;